import { FieldMapping } from '../services/organizationService';

/**
 * Default field mappings with source, writeToAsana, and promptConfig.
 * These are used as the initial template when creating a new organization.
 * 
 * Source types:
 * - iris: Extracted by IRIS agent from conversation
 * - prompt: Explicitly asked to the user via structured capture
 * - system: Auto-set by the system
 * - manual: Only set manually in Task Prioritization
 */
export const DEFAULT_FIELD_MAPPINGS: FieldMapping[] = [
  // IRIS fields — extracted from conversation by the AI agent
  { fieldName: 'problem_statement', asanaGid: '1211651287144358', label: 'Problem Statement', fieldType: 'text', source: 'iris', writeToAsana: true },
  { fieldName: 'desired_outcome', asanaGid: '1211735552208458', label: 'Desired Outcome', fieldType: 'text', source: 'iris', writeToAsana: true },
  { fieldName: 'business_impact', asanaGid: '1211651287144364', label: 'Business Impact', fieldType: 'text', source: 'iris', writeToAsana: true },
  { fieldName: 'theme', asanaGid: '1207163893235866', label: 'Theme', fieldType: 'enum', source: 'iris', writeToAsana: true },
  { fieldName: 'program_name', asanaGid: '1207163893039399', label: 'Program Name', fieldType: 'enum', source: 'iris', writeToAsana: true },
  { fieldName: 'program', asanaGid: '1207163893039399', label: 'Program', fieldType: 'enum', source: 'iris', writeToAsana: false },

  // Prompt fields — asked to the user during structured capture
  { fieldName: 'request_type', asanaGid: '1211722162573119', label: 'Request Type', fieldType: 'enum', source: 'prompt', writeToAsana: true, promptConfig: { question: 'What type of request is this?', inputType: 'chips', options: ['OP Request', 'In-Year'], required: true, order: 1 } },
  { fieldName: 'annualized_entitlement', asanaGid: '1207163892846980', label: 'Annualized Entitlement', fieldType: 'number', source: 'prompt', writeToAsana: true, promptConfig: { question: "What's the estimated annualized entitlement?", inputType: 'currency', required: true, order: 2 } },
  { fieldName: 'impact_metrics', asanaGid: '1211651287165115', label: 'Impact Metrics', fieldType: 'enum', source: 'prompt', writeToAsana: true, promptConfig: { question: 'How will we measure success?', inputType: 'chips', options: ['DEA', 'Speed', 'Productivity Savings', 'Transportation Savings', 'Quality', 'Safety', 'VCPU'], required: true, order: 3 } },

  // System fields — auto-set by the system
  { fieldName: 'created_at', asanaGid: '1211650324296514', label: 'Created At', fieldType: 'date', source: 'system', writeToAsana: true },
  { fieldName: 'created_by', asanaGid: '1211651287165137', label: 'Created By', fieldType: 'people', source: 'system', writeToAsana: true },
  { fieldName: 'op_decision', asanaGid: '1211663513934836', label: 'OP Decision', fieldType: 'enum', source: 'system', writeToAsana: true },
  { fieldName: 'target_year', asanaGid: '1211722162573117', label: 'Target Year', fieldType: 'text', source: 'system', writeToAsana: true },
  { fieldName: 'mission_fit', asanaGid: '1211663511894717', label: 'Mission Fit', fieldType: 'enum', source: 'system', writeToAsana: true },

  // Manual fields — set in Task Prioritization table, not written on task creation
  { fieldName: 'hc_sde', asanaGid: '1207163892815493', label: 'HC SDE', fieldType: 'number', source: 'manual', writeToAsana: false },
  { fieldName: 'l7_sdm', asanaGid: '1211651287144340', label: 'L7 SDM', fieldType: 'people', source: 'manual', writeToAsana: false },
  { fieldName: 'l7_manager', asanaGid: '1211651287144340', label: 'L7 Manager', fieldType: 'people', source: 'manual', writeToAsana: false },
  { fieldName: 'status', asanaGid: '1213084077750909', label: 'Status', fieldType: 'enum', source: 'manual', writeToAsana: false },
  { fieldName: 'launch_date', asanaGid: '1212385334334033', label: 'Launch Date', fieldType: 'date', source: 'manual', writeToAsana: false },
  { fieldName: 'rationale', asanaGid: '1211787647652709', label: 'Rationale', fieldType: 'text', source: 'manual', writeToAsana: false },
  { fieldName: 'key_deliverables', asanaGid: '1205737233045785', label: 'Key Deliverables', fieldType: 'text', source: 'manual', writeToAsana: false },

  // Other fields — less commonly used, default to iris/manual
  { fieldName: 'aft', asanaGid: '1207163317145561', label: 'AFT', fieldType: 'text', source: 'iris', writeToAsana: false },
  { fieldName: 'site_types', asanaGid: '1209389680616119', label: 'Site Types', fieldType: 'enum', source: 'iris', writeToAsana: false },
  { fieldName: 'mission', asanaGid: '1211663511894723', label: 'Mission', fieldType: 'text', source: 'manual', writeToAsana: false },
  { fieldName: 'impacted_region', asanaGid: '1207174268816145', label: 'Impacted Region', fieldType: 'text', source: 'iris', writeToAsana: false },
  { fieldName: 'aft_ob_team', asanaGid: '1203162011357977', label: 'AFT OB Team', fieldType: 'text', source: 'manual', writeToAsana: false },
  { fieldName: 'l6_sdm', asanaGid: '1211663513934983', label: 'L6 SDM', fieldType: 'text', source: 'manual', writeToAsana: false },
  { fieldName: 'director', asanaGid: '1211650324296508', label: 'Director', fieldType: 'text', source: 'manual', writeToAsana: false },
  { fieldName: 'product_domain', asanaGid: '1207163893235860', label: 'Product Domain', fieldType: 'text', source: 'manual', writeToAsana: false },
  { fieldName: 'impacted_process_path', asanaGid: '1211650324296496', label: 'Impacted Process Path', fieldType: 'text', source: 'manual', writeToAsana: false },
  { fieldName: 'impacted_process_path_alt', asanaGid: '1211663513934966', label: 'Impacted Process Path (Alt)', fieldType: 'text', source: 'manual', writeToAsana: false },
  { fieldName: 'l8', asanaGid: '1211663513935048', label: 'L8', fieldType: 'text', source: 'manual', writeToAsana: false },
  { fieldName: 'AFT Team', asanaGid: '1211707338971897', label: 'AFT Team', fieldType: 'text', source: 'manual', writeToAsana: false },
];

/**
 * All valid field types for the field mapping type dropdown.
 */
export const FIELD_TYPE_OPTIONS: FieldMapping['fieldType'][] = [
  'text',
  'number',
  'enum',
  'date',
  'people',
  'multi_enum',
];

/**
 * All valid source types for the field mapping source dropdown.
 */
export const FIELD_SOURCE_OPTIONS: NonNullable<FieldMapping['source']>[] = [
  'iris',
  'prompt',
  'system',
  'manual',
];

/**
 * All valid prompt input types.
 */
export const PROMPT_INPUT_TYPE_OPTIONS: NonNullable<NonNullable<FieldMapping['promptConfig']>['inputType']>[] = [
  'chips',
  'dropdown',
  'text',
  'number',
  'currency',
  'date',
];